import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReplayStop } from '../graphql/types';

export type AdjustMode = 'single' | 'range';
export type RangePhase = 'pickStart' | 'pickEnd' | 'pickDest';

export type LastMove =
    | { type: 'single'; fromNo: number; toNo: number }
    | { type: 'range'; loNo: number; hiNo: number; count: number; destNo: number; newStartNo: number; newEndNo: number };

/**
 * 手动重排逻辑，支持两种模式：
 *
 * 单点调整（single）：
 * - 点一个站设为锚点；再点另一个站 → 把它移到锚点正后面，序号重排，锚点前移到该站
 * - 再点锚点自身 / Esc / 点空白 → 结束
 *
 * 区间调整（range）：
 * - 点区间起点 → 点区间终点（起终点之间按当前序号连续的一整段自动纳入）
 * - 再点目的地 → 整段（段内顺序保持）移到目的站正后面，序号重排
 * - Esc / 点空白 → 取消当前选择
 *
 * 撤销/重做、恢复算法顺序两种模式共用。
 * 序号一律按数组下标 index+1 展示；起终点固定，不在此列。
 */
export function useManualReorder(baseStops: ReplayStop[], active: boolean, seedStops?: ReplayStop[] | null) {
    // 载入已保存版本时用 seed 作为初始顺序，否则用算法顺序
    const initial = seedStops ?? baseStops;
    const [history, setHistory] = useState<ReplayStop[][]>([initial]);
    const [hIdx, setHIdx] = useState(0);
    const [mode, setModeState] = useState<AdjustMode>('single');
    // 单点：锚点
    const [anchorKey, setAnchorKey] = useState<string | null>(null);
    // 区间：起点 / 终点
    const [rangeStartKey, setRangeStartKey] = useState<string | null>(null);
    const [rangeEndKey, setRangeEndKey] = useState<string | null>(null);
    const [lastMove, setLastMove] = useState<LastMove | null>(null);

    // 进入手动 / 换路线 / 载入版本 → 全量重置
    useEffect(() => {
        setHistory([initial]);
        setHIdx(0);
        setModeState('single');
        setAnchorKey(null);
        setRangeStartKey(null);
        setRangeEndKey(null);
        setLastMove(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseStops, active, seedStops]);

    const order = history[hIdx] ?? initial;

    const commit = useCallback(
        (next: ReplayStop[]) => {
            setHistory((h) => [...h.slice(0, hIdx + 1), next]);
            setHIdx((i) => i + 1);
        },
        [hIdx]
    );

    // 区间当前选中段（按当前顺序连续）
    const range = useMemo(() => {
        if (mode !== 'range' || !rangeStartKey) return null;
        const si = order.findIndex((s) => s.stop === rangeStartKey);
        if (si < 0) return null;
        if (!rangeEndKey) return { lo: si, hi: si, keys: [order[si].stop] };
        const ei = order.findIndex((s) => s.stop === rangeEndKey);
        if (ei < 0) return { lo: si, hi: si, keys: [order[si].stop] };
        const lo = Math.min(si, ei);
        const hi = Math.max(si, ei);
        return { lo, hi, keys: order.slice(lo, hi + 1).map((s) => s.stop) };
    }, [mode, rangeStartKey, rangeEndKey, order]);

    const rangePhase: RangePhase | null = useMemo(() => {
        if (mode !== 'range') return null;
        if (!rangeStartKey) return 'pickStart';
        if (!rangeEndKey) return 'pickEnd';
        return 'pickDest';
    }, [mode, rangeStartKey, rangeEndKey]);

    // 单点：把点击的（连续）块移到锚点正后面。keys 为该 marker 背后的 stop 列表
    // （普通点=单元素；重合簇=整簇连续 key，整簇作为一个位置一起移动）
    const clickSingle = useCallback(
        (keys: string[]) => {
            if (anchorKey == null) {
                // 锚定簇尾：后续被移动的点会插到「整簇之后」
                setAnchorKey(keys[keys.length - 1]);
                setLastMove(null);
                return;
            }
            if (keys.includes(anchorKey)) {
                // 点回锚定的簇自身 → 取消锚定
                setAnchorKey(null);
                return;
            }
            const cur = [...order];
            const idxs = keys
                .map((k) => cur.findIndex((s) => s.stop === k))
                .filter((i) => i >= 0)
                .sort((a, b) => a - b);
            if (!idxs.length) return;
            const lo = idxs[0];
            const hi = idxs[idxs.length - 1];
            const fromNo = lo + 1;
            const block = cur.slice(lo, hi + 1);
            const rest = [...cur.slice(0, lo), ...cur.slice(hi + 1)];
            const anchorIdx = rest.findIndex((s) => s.stop === anchorKey);
            if (anchorIdx < 0) return;
            const next = [...rest.slice(0, anchorIdx + 1), ...block, ...rest.slice(anchorIdx + 1)];
            commit(next);
            setLastMove({ type: 'single', fromNo, toNo: anchorIdx + 2 });
            // 锚点前移到刚移动这一簇的簇尾，可继续往后接排
            setAnchorKey(keys[keys.length - 1]);
        },
        [anchorKey, order, commit]
    );

    // 区间：起点 → 终点 → 目的地。keys 为该 marker 背后的 stop 列表（重合簇取首/尾自然纳入整簇）
    const clickRange = useCallback(
        (keys: string[]) => {
            const first = keys[0];
            const last = keys[keys.length - 1];
            if (!rangeStartKey) {
                setRangeStartKey(first); // 起点取簇首
                setLastMove(null);
                return;
            }
            if (!rangeEndKey) {
                // 再点起点自身 → 视为单站区间；正常点另一站 → 定终点（取簇尾）
                setRangeEndKey(last);
                return;
            }
            // 点目的地
            if (!range) return;
            if (keys.some((k) => range.keys.includes(k))) return; // 目的地不能在区间内
            const destKey = last; // 落到「整簇之后」
            const rangeLen = range.hi - range.lo + 1;
            const loNo = range.lo + 1;
            const hiNo = range.hi + 1;
            const rangeItems = order.slice(range.lo, range.hi + 1);
            const rest = [...order.slice(0, range.lo), ...order.slice(range.hi + 1)];
            const destNewIdx = rest.findIndex((s) => s.stop === destKey);
            if (destNewIdx < 0) return;
            const next = [...rest.slice(0, destNewIdx + 1), ...rangeItems, ...rest.slice(destNewIdx + 1)];
            commit(next);
            const newStartNo = destNewIdx + 2;
            const newEndNo = newStartNo + rangeLen - 1;
            setLastMove({ type: 'range', loNo, hiNo, count: rangeLen, destNo: destNewIdx + 1, newStartNo, newEndNo });
            // 完成一次移动 → 回到「点起点」重新开始
            setRangeStartKey(null);
            setRangeEndKey(null);
        },
        [rangeStartKey, rangeEndKey, range, order, commit]
    );

    // 最近点击的站（含自增 seq，供右侧列表滚动定位；同一站连点也能触发滚动）
    const [focus, setFocus] = useState<{ key: string; seq: number } | null>(null);
    const clickStop = useCallback(
        (keysOrKey: string[] | string) => {
            const keys = Array.isArray(keysOrKey) ? keysOrKey : [keysOrKey];
            if (!keys.length) return;
            setFocus((prev) => ({ key: keys[0], seq: (prev?.seq ?? 0) + 1 }));
            return mode === 'range' ? clickRange(keys) : clickSingle(keys);
        },
        [mode, clickRange, clickSingle]
    );

    // Esc / 点空白：取消当前模式的选择
    const cancelSelection = useCallback(() => {
        setAnchorKey(null);
        setRangeStartKey(null);
        setRangeEndKey(null);
    }, []);

    // 切换 单点/区间 tab → 保留当前已调整顺序（不重置回算法），只清进行中的选择
    const setMode = useCallback((m: AdjustMode) => {
        setModeState(m);
        setAnchorKey(null);
        setRangeStartKey(null);
        setRangeEndKey(null);
        setLastMove(null);
    }, []);

    const undo = useCallback(() => {
        setHIdx((i) => Math.max(0, i - 1));
        cancelSelection();
        setLastMove(null);
    }, [cancelSelection]);

    const redo = useCallback(() => {
        setHIdx((i) => Math.min(history.length - 1, i + 1));
        cancelSelection();
    }, [history.length, cancelSelection]);

    const reset = useCallback(() => {
        setHistory([baseStops]);
        setHIdx(0);
        cancelSelection();
        setLastMove(null);
    }, [baseStops, cancelSelection]);

    return {
        order,
        mode,
        setMode,
        anchorKey,
        rangeKeys: range?.keys ?? [],
        rangePhase,
        rangeInfo: range ? { loNo: range.lo + 1, hiNo: range.hi + 1, count: range.hi - range.lo + 1 } : null,
        lastMove,
        focus,
        canUndo: hIdx > 0,
        canRedo: hIdx < history.length - 1,
        clickStop,
        cancelSelection,
        undo,
        redo,
        reset,
    };
}
